import React, { Fragment, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { MuiThemeProvider, Button, Typography } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import ReactGA from 'react-ga4';
import { axuminTheme, darkGrey, universalStyles, white, borderGrey, buttonText } from '../../../app/theme';
import { BASE_URL, IMAGE_PATHS } from '../../../constants/paths.constants';
import AxuminTrainingNavBar from '../../micro/TrainingNavBar/AxuminTrainingNavBar';
import AxuminNavBar from '../../micro/BrandNavBar/AxuminNavBar';
import Indication from '../../micro/Indication/AxuminIndication';
import TrainingFooter from '../../micro/TrainingFooter/TrainingFooter';
import DOWNLOADABLE_RESOURCES from '../../../constants/downloads.constants';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  grayBox: {
    backgroundColor: darkGrey,
    width: '100%',
    margin: 0,
    paddingBottom: 56,
  },
  content: {
    maxWidth: 1242,
    margin: 'auto',
  },
  header: {
    padding: '50px 0 10px',
  },
  text: {
    color: white,
  },
  yellowHeader: {
    margin: '55px 0 30px',
  },
  whiteRow: {
    backgroundColor: white,
    border: `6px solid ${borderGrey}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 40px',
    marginBottom: 15,
  },
  leftSide: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  pdf: {
    width: 36,
    marginRight: 25,
  },
  button: {
    width: 174,
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 24,
    color: buttonText,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    marginRight: 5,
  },
}));

const AxuminDownloadableResources = (): JSX.Element => {
  const classes = useStyles();
  useEffect(() => {
    document.title = 'Downloadable Resources';
  }, []);

  const handleDownload = (action: string) => {
    ReactGA.event({
      category: 'event',
      action,
    });
  };

  return (
    <MuiThemeProvider theme={axuminTheme}>
      <AxuminTrainingNavBar />
      <AxuminNavBar section="Downloadable Resources" />
      <div className={classes.grayBox}>
        <div className={classes.content}>
          <Typography variant="h1" className={classes.header}>
            Downloadable Resources
          </Typography>
          <Typography variant="body1" className={classes.text}>
            The following resources help nuclear medicine physicians, technologists, and professionals in related fields
            learn more about Axumin image acquisition and image interpretation. All materials can be downloaded to your
            desktop and used outside of this browser.
          </Typography>
          {Object.keys(DOWNLOADABLE_RESOURCES.axuminResources).map((key) => (
            <Fragment key={key}>
              <Typography variant="h6" className={classes.yellowHeader}>
                {key}
              </Typography>
              {DOWNLOADABLE_RESOURCES.axuminResources[key].map((download) => (
                <div className={classes.whiteRow} key={download.title}>
                  <div className={classes.leftSide}>
                    <img
                      className={classes.pdf}
                      src={`${BASE_URL}${IMAGE_PATHS.AXUMIN_PDF_RESOURCES_ICON.path}`}
                      alt={IMAGE_PATHS.AXUMIN_PDF_RESOURCES_ICON.alt}
                    />
                    <Typography variant="body2">
                      <b>{download.title}</b>
                    </Typography>
                  </div>
                  <Button
                    onClick={() => handleDownload(download.eventName)}
                    href={download.path}
                    target="_blank"
                    variant="contained"
                    color="primary"
                    className={classes.button}
                  >
                    <GetAppIcon className={classes.icon} />
                    DOWNLOAD
                  </Button>
                </div>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
      <Indication />
      <TrainingFooter />
    </MuiThemeProvider>
  );
};

export default AxuminDownloadableResources;
